/**
 * WeatherDayPresenter - Today/Tomorrow weather view presenter
 *
 * NOTE: This presenter component is separated from WeatherDayWidget.tsx for Module Federation.
 * It contains the same UI logic but receives all data via props instead of MobX injection.
 * This allows JHP to consume it via Module Federation while Zoomin's WeatherDayWidget.tsx
 * continues to use MobX stores for data. Both components should render identically.
 */

import * as React from 'react'

import { classList } from 'react-classlist-helper'

import OpenWeatherIcon from '~/client/src/shared/components/OpenWeatherIcon'
import { IWeatherBarForecast } from '~/client/src/shared/components/WeatherHourlyWidget/WeatherBarPresenter'
import WeatherHourlyPresenter from '~/client/src/shared/components/WeatherHourlyWidget/WeatherHourlyPresenter'
import * as WeatherIcon from '~/client/src/shared/components/WeatherIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import CityAndDatePresenter, { IProjectAddress } from './CityAndDatePresenter'
import DataPointPresenter from './DataPointPresenter'

import './WeatherDayPresenter.scss'

export interface IWeatherDayForecast {
  /** Current/average temperature */
  temperature: number
  /** Minimum temperature for the day */
  minTemperature: number
  /** Maximum temperature for the day */
  maxTemperature: number
  /** Humidity percentage (0-100) */
  humidity: number
  /** Wind speed in the appropriate unit */
  windSpeed: number
  /** Wind direction in degrees */
  windDegree: number
  /** Probability of precipitation (0-100) */
  probabilityOfPrecipitation: number
  /** Precipitation amount in mm */
  amountOfPrecipitation: number
  /** OpenWeather icon code (e.g., "01d", "10n") */
  weatherIconCode: string
  /** Weather description key (e.g., "clear sky") */
  weatherDescription: string
}

export interface IWeatherDayPresenterProps {
  /** Forecast data for the day */
  forecast: IWeatherDayForecast
  /** Hourly forecasts for the day */
  hourlyForecasts: IWeatherBarForecast[]
  /** Project address for display */
  projectAddress: IProjectAddress
  /** Display units */
  units: 'imperial' | 'metric'
  /** Formatted date string to display */
  formattedDate: string
  /** Format time for display - returns [time, period] e.g. ["3", "PM"] */
  formatTime?: (timestamp: number) => [string, string]

  /** Whether it's constrained horizontally */
  compactH?: boolean
  /** Whether it's constrained vertically */
  compactV?: boolean
}

const ICON_SIZE_SMALL = 10
const ICON_SIZE_LARGE = 32
const MAX_BAR_HEIGHT = 200
const MAX_BAR_HEIGHT_COMPACT = 160
const MIN_BAR_HEIGHT = 135
const MIN_BAR_HEIGHT_COMPACT = 120

/**
 * Format precipitation value with appropriate precision
 */
function littlePrecision(val: number): string {
  if (val < 0.1) return ''
  if (val < 1) return val.toFixed(1)
  return Math.round(val).toString()
}

/**
 * Convert millimeters to inches
 */
function mmToInch(mm: number): number {
  return mm * 0.0394
}

/**
 * Get temperature unit label
 */
function temperatureUnits(units: 'imperial' | 'metric'): string {
  return units === 'imperial' ? '°F' : '°C'
}

/**
 * Get wind speed unit label
 */
function windSpeedUnits(units: 'imperial' | 'metric'): string {
  return units === 'imperial' ? 'mph' : 'm/s'
}

export default function WeatherDayPresenter({
  forecast,
  hourlyForecasts,
  projectAddress,
  units,
  formattedDate,
  formatTime,
  compactH,
  compactV,
}: IWeatherDayPresenterProps): JSX.Element | null {
  if (!forecast) {
    return null
  }

  const tempUnit = temperatureUnits(units)
  const windUnit = windSpeedUnits(units)

  // Get weather description translation
  const weatherDescription =
    Localization.translator.weatherDescriptions?.[
      forecast.weatherDescription
    ] ?? forecast.weatherDescription

  return (
    <div
      className={classList({
        'col weather-day-widget full-width justify-around': true,
        'gap-16': !compactV,
        'gap-4': compactV,
      })}
    >
      <div
        className={classList({
          'x-around y-start flex-grow-0': true,
          'row gap-16': !compactH,
          'col gap-4': compactH,
          mh120: !compactV,
          'min-height90': compactV,
        })}
      >
        <div
          className={classList({
            col: true,
            'gap-8': !compactH && !compactV,
            'gap-2': compactH || compactV,
          })}
        >
          <div className="row full-width h-fit-content">
            <div className="h24">
              <OpenWeatherIcon
                openWeatherIconCode={forecast.weatherIconCode}
                isColor
                className="icon-w24 icon-h24 float-left"
              />
              <span className="text extra-large bold line-24 ml4">
                {weatherDescription}
              </span>
            </div>
          </div>
          <div
            className={classList({
              'row full-width full-height': true,
              'gap-4': compactH,
              'gap-16': !compactH,
            })}
          >
            <div
              className={classList({
                'col w-fit-content': true,
                'gap-4': compactH,
                'gap-16': !compactH,
              })}
            >
              <div
                className={classList({
                  'col text black bold lpMinus1': true,
                  'size44 h50 line-50': !compactH && !compactV,
                  'size27 h32 line-32': compactH || compactV,
                  // 'size22 h32': !compactH && compactV,
                  // 'size18 h28': compactH && compactV,
                })}
              >
                {forecast.temperature}
                {tempUnit}
              </div>
            </div>
            <div
              className={classList({
                'col as-stretch': true,
                'gap-4': compactH,
                'gap-16': !compactH,
              })}
            >
              <div
                className={classList({
                  'col y-around': true,
                  h50: !compactH && !compactV,
                  h32: compactH || compactV,
                  // h32: !compactH && compactV,
                  // h28: compactH && compactV,
                })}
              >
                <div>
                  <span className="text grey-light mr4">
                    {Localization.translator.temperatureRange?.high}
                  </span>
                  <span className="text bold grey-10">
                    {forecast.maxTemperature}
                    {tempUnit}
                  </span>
                </div>
                <div>
                  <span className="text grey-light mr4">
                    {Localization.translator.temperatureRange?.low}
                  </span>
                  <span className="text bold grey-10">
                    {forecast.minTemperature}
                    {tempUnit}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="row x-start">
            <div
              className={classList({
                'row h24 flex-grow-0': true,
                'gap-6': compactH,
                'gap-12': !compactH,
              })}
            >
              <DataPointPresenter
                icon={
                  <WeatherIcon.WindDirection
                    size={ICON_SIZE_SMALL}
                    className="text grey-light mb4"
                    style={{ transform: `rotate(${forecast.windDegree}deg)` }}
                  />
                }
                value={forecast.windSpeed.toString()}
                label={windUnit}
              />
              <DataPointPresenter
                icon={
                  <WeatherIcon.Humidity className="text grey-light inline-block" />
                }
                value={`${forecast.humidity}%`}
                label={Localization.translator.humidity as string}
              />
              <DataPointPresenter
                icon={
                  <WeatherIcon.DewPoint className="text grey-light inline-block" />
                }
                value={
                  units === 'imperial'
                    ? `${
                        littlePrecision(
                          mmToInch(forecast.amountOfPrecipitation),
                        ) || 0
                      }"`
                    : `${
                        littlePrecision(forecast.amountOfPrecipitation) || 0
                      }mm`
                }
                label={Localization.translator.precipitation as string}
              />
            </div>
          </div>
        </div>
        {projectAddress?.city && projectAddress?.state && (
          <CityAndDatePresenter
            projectAddress={projectAddress}
            date={formattedDate}
            compactH={compactH}
            compactV={compactV}
          />
        )}
      </div>

      {hourlyForecasts && hourlyForecasts.length > 0 && (
        <div className="row">
          <WeatherHourlyPresenter
            forecasts={hourlyForecasts}
            units={units}
            formatTime={formatTime}
            iconSize={ICON_SIZE_LARGE}
            maxBarHeight={compactV ? MAX_BAR_HEIGHT_COMPACT : MAX_BAR_HEIGHT}
            minBarHeight={compactV ? MIN_BAR_HEIGHT_COMPACT : MIN_BAR_HEIGHT}
            className="mb0"
            compactH={compactH}
            compactV={compactV}
          />
        </div>
      )}
    </div>
  )
}
